const PAYMENT_RETURN_TYPES = new Set(["CardPayment", "Wallet", "ZainCash"]);

export function cleanPaymentType(raw: string | null): string {
  if (!raw) return "";
  const qIdx = raw.indexOf("?");
  return qIdx >= 0 ? raw.slice(0, qIdx) : raw;
}

/** ZainCash often appends `?token=` onto a URL that already has `?`, so extra `?` become `&`. */
export function normalizePaymentQueryString(search: string): URLSearchParams {
  const raw = search.startsWith("?") ? search.slice(1) : search;
  return new URLSearchParams(raw.replace(/\?/g, "&"));
}

export function extractEmbeddedPaymentToken(searchParams: URLSearchParams): string | null {
  const fromQuery = searchParams.get("token");
  if (fromQuery) return fromQuery;
  const rawType = searchParams.get("type") ?? "";
  const qIdx = rawType.indexOf("?");
  if (qIdx < 0) return null;
  return new URLSearchParams(rawType.slice(qIdx + 1)).get("token");
}

export function getPaymentCallbackId(searchParams: URLSearchParams): string {
  return String(
    searchParams.get("id") ??
      searchParams.get("checkoutId") ??
      searchParams.get("transactionId") ??
      searchParams.get("transactionid") ??
      searchParams.get("txnId") ??
      searchParams.get("txnid") ??
      searchParams.get("paymentId") ??
      searchParams.get("paymentid") ??
      ""
  ).trim();
}

export function hasPendingZainCashWalletTopUp(): boolean {
  return (
    localStorage.getItem("lastZainCashWalletTopUp") === "1" &&
    Boolean((localStorage.getItem("lastZainCashTransactionId") ?? "").trim())
  );
}

/** Recharge create id first — same as the mobile app. JWT `id` is a different ZainCash field. */
export function resolveZainCashTransactionId(decoded?: {
  id?: string;
  transactionId?: string;
  transactionID?: string;
  txnId?: string;
  paymentId?: string;
} | null): string {
  const stored = (localStorage.getItem("lastZainCashTransactionId") ?? "").trim();
  if (localStorage.getItem("lastZainCashWalletTopUp") === "1" && stored) {
    return stored;
  }
  const fromDecoded = [
    decoded?.transactionId,
    decoded?.transactionID,
    decoded?.txnId,
    decoded?.paymentId,
    decoded?.id,
  ]
    .map((value) => String(value ?? "").trim())
    .find(Boolean);
  return stored || fromDecoded || "";
}

/** True only after a gateway redirect — not for stale ?type= left in the URL. */
export function isPaymentReturnUrl(searchParams: URLSearchParams): boolean {
  if (hasPendingZainCashWalletTopUp()) return true;

  const type = cleanPaymentType(searchParams.get("type"));
  if (!type || !PAYMENT_RETURN_TYPES.has(type)) return false;

  if (searchParams.has("paymentStatus")) return true;
  if (getPaymentCallbackId(searchParams)) return true;
  if (type === "ZainCash" && extractEmbeddedPaymentToken(searchParams)) return true;

  return false;
}

export function isOppwaStaleSessionMessage(message: string | undefined): boolean {
  const text = String(message ?? "").toLowerCase();
  return (
    text.includes("no payment session found") ||
    text.includes("mixing test/live") ||
    text.includes("more than 30min")
  );
}

export function stripPaymentQueryKeys(params: URLSearchParams): URLSearchParams {
  const next = new URLSearchParams(params);
  [
    "paymentStatus",
    "type",
    "id",
    "checkoutId",
    "returnTo",
    "token",
    "transactionId",
    "transactionid",
    "txnId",
    "txnid",
    "paymentId",
    "paymentid",
    "resourcePath",
  ].forEach((key) => next.delete(key));
  return next;
}
